/**
 * Deterministic randomness for the painters: integer hashing, seeded random
 * streams and smooth 1-D value noise for terrain. Everything is a pure
 * function of its seed, so a theme always paints the same landscape and the
 * static and moving passes of a layer agree on where things are.
 */
import { deriveSeed, Rng } from '@dropstack/core';

/** 32-bit FNV-1a hash of a string (theme ids → seeds). */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Uniform [0, 1) value for integer lattice point `i` of stream `seed`. */
export function hash01(seed: number, i: number): number {
  return deriveSeed(seed, i | 0) / 4294967296;
}

/** A seeded random stream (mulberry32 via core's Rng). */
export function rngFor(seed: number, salt = 0): Rng {
  return new Rng(deriveSeed(seed, salt));
}

/** Uniform float in [lo, hi) from a stream. */
export function range(rng: Rng, lo: number, hi: number): number {
  return lo + (hi - lo) * rng.next();
}

/**
 * Smooth 1-D value noise in [-1, 1]: hashed values on integer lattice points,
 * blended with a quintic fade so slopes are continuous (no creases in hills).
 */
export function valueNoise(seed: number, x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const t = f * f * f * (f * (f * 6 - 15) + 10);
  const a = hash01(seed, i) * 2 - 1;
  const b = hash01(seed, i + 1) * 2 - 1;
  return a + (b - a) * t;
}
